import { useEffect, useMemo, useRef, useState } from "react";
import type { ProjectManifest, ProjectResp } from "@pix/shared";
import { api, type PublishedWork } from "../api/client";
import { listAssets, type AssetRecord } from "../storage/assetStore";

interface Props {
  project: ProjectResp;
  manifest: ProjectManifest;
  onClose: () => void;
  onLog: (line: string) => void;
}

const GENRES = [
  { key: "romance", ja: "恋愛" },
  { key: "mystery", ja: "ミステリ" },
  { key: "comedy", ja: "コメディ" },
  { key: "horror", ja: "ホラー" },
  { key: "fantasy", ja: "ファンタジー" },
  { key: "scifi", ja: "SF" },
  { key: "slice-of-life", ja: "日常" },
  { key: "other", ja: "その他" },
];
const AGES = [
  { key: "all", label: "全年齢" },
  { key: "r15", label: "R-15" },
  { key: "r18", label: "R-18" },
];

const PORTAL_URL =
  (import.meta.env.VITE_PORTAL_URL as string | undefined) ?? "https://pixadv.net";

export function PublishModal({ project, manifest, onClose, onLog }: Props) {
  const [loading, setLoading] = useState(true);
  const [existing, setExisting] = useState<PublishedWork | null>(null);
  const [title, setTitle] = useState(manifest.title || project.name);
  const [shortDescription, setShortDescription] = useState("");
  const [description, setDescription] = useState("");
  const [genre, setGenre] = useState("other");
  const [ageRating, setAgeRating] = useState("all");
  const [price, setPrice] = useState(0);
  const [estimatedPlayTime, setEstimatedPlayTime] = useState<number | "">("");
  const [tags, setTags] = useState("");
  const [isPublished, setIsPublished] = useState(true);
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  const [screenshots, setScreenshots] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageAssets, setImageAssets] = useState<AssetRecord[]>([]);

  useEffect(() => {
    let canceled = false;
    api
      .fetchPublishedWork(project.id)
      .then((w) => {
        if (canceled) return;
        if (w) {
          setExisting(w);
          setTitle(w.title);
          setShortDescription(w.shortDescription);
          setDescription(w.description ?? "");
          setGenre(w.genre);
          setAgeRating(w.ageRating);
          setPrice(w.price);
          setEstimatedPlayTime(w.estimatedPlayTime ?? "");
          setTags(w.tags.join(", "));
          setIsPublished(w.isPublished);
          setThumbnail(w.thumbnail ?? null);
          setScreenshots(w.screenshots ?? []);
        }
      })
      .catch((e) => {
        if (!canceled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!canceled) setLoading(false);
      });
    return () => {
      canceled = true;
    };
  }, [project.id]);

  // プロジェクト内の画像アセット一覧を読み込む
  useEffect(() => {
    let canceled = false;
    void listAssets(project.id).then((all) => {
      if (canceled) return;
      setImageAssets(all.filter((a) => a.mime.startsWith("image/")));
    });
    return () => {
      canceled = true;
    };
  }, [project.id]);

  // プレビュー画像 URL マップ (アンマウントで revoke)
  const objectUrls = useRef<Map<string, string>>(new Map());
  const imageUrlByPath = useMemo(() => {
    const m = new Map<string, string>();
    const seen = new Set<string>();
    for (const a of imageAssets) {
      let url = objectUrls.current.get(a.path);
      if (!url) {
        url = URL.createObjectURL(a.blob);
        objectUrls.current.set(a.path, url);
      }
      m.set(a.path, url);
      seen.add(a.path);
    }
    // 不要になった URL を revoke
    for (const [k, v] of objectUrls.current) {
      if (!seen.has(k)) {
        URL.revokeObjectURL(v);
        objectUrls.current.delete(k);
      }
    }
    return m;
  }, [imageAssets]);
  useEffect(
    () => () => {
      for (const v of objectUrls.current.values()) URL.revokeObjectURL(v);
      objectUrls.current.clear();
    },
    [],
  );

  function toggleScreenshot(path: string) {
    setScreenshots((cur) => {
      if (cur.includes(path)) return cur.filter((p) => p !== path);
      if (cur.length >= 6) {
        alert("スクリーンショットは最大 6 枚までです");
        return cur;
      }
      return [...cur, path];
    });
  }

  async function submit() {
    setError(null);
    setSaving(true);
    try {
      const tagList = tags
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const res = await api.publishWork({
        projectId: project.id,
        title: title.trim(),
        shortDescription: shortDescription.trim() || undefined,
        description: description || undefined,
        genre,
        ageRating,
        price,
        estimatedPlayTime: typeof estimatedPlayTime === "number" ? estimatedPlayTime : undefined,
        tags: tagList,
        isPublished,
        thumbnail,
        screenshots,
      });
      onLog(
        `[publish] ${isPublished ? "公開しました" : "下書き保存しました"}: ${res.work?.id ?? project.id}`,
      );
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  async function unpublish() {
    if (!confirm("この作品を Portal から取り下げますか?(プロジェクト自体は残ります)"))
      return;
    setSaving(true);
    try {
      await api.unpublishWork(project.id);
      onLog("[publish] 取り下げました");
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div
        className="modal modal-publish"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2>
          {existing ? "Portal で公開中の作品を編集" : "Portal に公開する"}
          {existing && (
            <span
              style={{
                fontSize: 11,
                fontFamily: "monospace",
                marginLeft: 10,
                color: existing.isPublished ? "var(--success)" : "var(--muted-2)",
              }}
            >
              ● {existing.isPublished ? "公開中" : "下書き"}
            </span>
          )}
        </h2>

        {loading ? (
          <div style={{ padding: 20, color: "var(--muted-2)" }}>読み込み中…</div>
        ) : (
          <div className="publish-form">
            <Row label="タイトル">
              <input value={title} onChange={(e) => setTitle(e.target.value)} />
            </Row>
            <Row label="短い説明 (80字程度)">
              <input
                value={shortDescription}
                onChange={(e) => setShortDescription(e.target.value)}
                maxLength={120}
                placeholder="検索結果やカードに表示される 1 行"
              />
            </Row>
            <Row label="説明 (Markdown)">
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={6}
                placeholder="## あらすじ&#10;..."
              />
            </Row>
            <Row label="ジャンル">
              <select value={genre} onChange={(e) => setGenre(e.target.value)}>
                {GENRES.map((g) => (
                  <option key={g.key} value={g.key}>
                    {g.ja}
                  </option>
                ))}
              </select>
            </Row>
            <Row label="年齢レーティング">
              <select value={ageRating} onChange={(e) => setAgeRating(e.target.value)}>
                {AGES.map((a) => (
                  <option key={a.key} value={a.key}>
                    {a.label}
                  </option>
                ))}
              </select>
            </Row>
            <Row label="価格 (JPY、0 で無料)">
              <input
                type="number"
                value={price}
                onChange={(e) => setPrice(Number(e.target.value) || 0)}
                min={0}
                step={100}
              />
            </Row>
            <Row label="プレイ時間 (分)">
              <input
                type="number"
                value={estimatedPlayTime}
                onChange={(e) =>
                  setEstimatedPlayTime(
                    e.target.value === "" ? "" : Number(e.target.value),
                  )
                }
                min={0}
              />
            </Row>
            <Row label="タグ (カンマ区切り)">
              <input
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="百合, ミステリ, 切ない"
              />
            </Row>

            <Row label="キービジュアル">
              <div className="pub-thumb-picker">
                <div className="pub-thumb-preview">
                  {thumbnail && imageUrlByPath.get(thumbnail) ? (
                    <img src={imageUrlByPath.get(thumbnail)} alt={thumbnail} />
                  ) : (
                    <div className="pub-thumb-empty">未選択</div>
                  )}
                </div>
                <div className="pub-thumb-options">
                  {imageAssets.length === 0 ? (
                    <div className="pub-asset-empty">
                      ASSETS タブから画像をアップロードしてください
                    </div>
                  ) : (
                    <div className="pub-asset-grid">
                      <button
                        type="button"
                        className={`pub-asset-tile ${thumbnail === null ? "active" : ""}`}
                        onClick={() => setThumbnail(null)}
                        title="サムネを指定しない"
                      >
                        <div className="pub-asset-noimg">なし</div>
                      </button>
                      {imageAssets.map((a) => (
                        <button
                          key={a.path}
                          type="button"
                          className={`pub-asset-tile ${thumbnail === a.path ? "active" : ""}`}
                          onClick={() => setThumbnail(a.path)}
                          title={a.path}
                        >
                          <img src={imageUrlByPath.get(a.path)} alt={a.path} />
                          <span className="pub-asset-path">{a.path}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Row>

            <Row label={`スクショ (${screenshots.length}/6)`}>
              {imageAssets.length === 0 ? (
                <div className="pub-asset-empty">
                  画像アセットがありません
                </div>
              ) : (
                <div className="pub-asset-grid">
                  {imageAssets.map((a) => {
                    const idx = screenshots.indexOf(a.path);
                    return (
                      <button
                        key={a.path}
                        type="button"
                        className={`pub-asset-tile ${idx >= 0 ? "active" : ""}`}
                        onClick={() => toggleScreenshot(a.path)}
                        title={a.path}
                      >
                        <img src={imageUrlByPath.get(a.path)} alt={a.path} />
                        {idx >= 0 && (
                          <span className="pub-asset-badge">{idx + 1}</span>
                        )}
                        <span className="pub-asset-path">{a.path}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </Row>

            <Row label="ステータス">
              <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input
                  type="checkbox"
                  checked={isPublished}
                  onChange={(e) => setIsPublished(e.target.checked)}
                />
                公開する(チェックを外すと下書きとして保存)
              </label>
            </Row>

            {error && <div className="publish-error">{error}</div>}

            {existing?.isPublished && (
              <div className="publish-link-note">
                公開 URL:{" "}
                <a
                  href={`${PORTAL_URL}/works/${project.id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {PORTAL_URL}/works/{project.id}
                </a>
              </div>
            )}
          </div>
        )}

        <div className="modal-actions modal-actions-spread">
          <div>
            {existing && (
              <button
                className="btn btn-warn btn-sm"
                onClick={() => void unpublish()}
                disabled={saving}
              >
                取り下げる
              </button>
            )}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
              キャンセル
            </button>
            <button
              className="btn btn-accent"
              onClick={() => void submit()}
              disabled={saving || !title.trim()}
            >
              {existing ? "更新する" : isPublished ? "公開する" : "保存する"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="publish-row">
      <label>{label}</label>
      <div>{children}</div>
    </div>
  );
}
